import "./CdeCatalog.css";

import { useEffect, useState } from "react";
import { getInput } from "../../api/inputApi";
import { getSearchResult } from "../../api/searchApi";
import BodyHeader from "../Layouts/BodyHeader/BodyHeader";
import ViewHeader from "../Layouts/ViewHeader/ViewHeader";
import InputSelect from "../Layouts/InputField/InputSelect/inputSelect";
import InputText from "../Layouts/InputField/InputText/inputText";
import Spinner from "../Layouts/Spinner/spinner";
import ResultsTable from "../Layouts/ResultsTable/resultsTable";
import InputList from "../Layouts/InputField/InputList/inputList";
import FormBuilderCart from "../FormBuilder/cart/formBuilderCart";
import NotificationBar from "../Layouts/Notification/notificationBar";
import useNotifications from "../../hooks/useNotifications";

const defaultFormData = {
  disease: "None",
  subDisease: "",
  domain: "",
  subDomain: "",
  crf: "",
  copyright: "",
  classification: "",
  population: "",
  cde: "",
  keyword: "",
};

// Fetch input data once and store it in state
const fetchData = async () => {
  const diseaseInput = await getInput("disease");
  const subDiseaseInput = await getInput("subDisease");
  const domainInput = await getInput("domain");
  const subDomainInput = await getInput("subDomain");
  const crfInput = await getInput("crf");
  return {
    diseaseInput,
    subDiseaseInput,
    domainInput,
    subDomainInput,
    crfInput,
  };
};

export default function CdeCatalog() {
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [inputs, setInputs] = useState({
    diseaseInput: [],
    subDiseaseInput: [],
    domainInput: [],
    subDomainInput: [],
    crfInput: [],
  });
  const [dropdowns, setDropdowns] = useState({
    diseasesDrpDwn: [],
    subDiseasesDrpDwn: [],
    domainsDrpDwn: [],
    subDomainsDrpDwn: [],
  });

  const [disableSubDisease, setDisableSubDisease] = useState(true);
  const [disableSubDomain, setDisableSubDomain] = useState(true);

  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const { notifications, removeNotification, ...restNotifications } = useNotifications();

  useEffect(() => {
    fetchData().then((data) => {
      setInputs(data);
      setDropdowns({
        diseasesDrpDwn: data.diseaseInput,
        subDiseasesDrpDwn: data.subDiseaseInput,
        domainsDrpDwn: data.domainInput,
        subDomainsDrpDwn: data.subDomainInput,
      });
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const doSearch = async () => {
      setIsSearching(true);
      setSearchResults(null);

      const searchResult = await getSearchResult("CdeCatalog", formData);

      if (!cancelled)
        setSearchResults(searchResult);
      setIsSearching(false);
    };

    if (formData.disease === 'None')
      return;

    doSearch();

    return () => {
      cancelled = true;
    }
  }, [formData.disease]);

  let filteredSubDiseases = [];
  let filteredSubDomains = [];

  const handleChange = (e, type, val) => {
    const value = type === 'disease' ? val : e.target.value;
    setFormData({ ...formData, [type]: value });

    switch (type) {
      case "disease":
        if (value === "") {
          setDisableSubDisease(true);
          filteredSubDiseases = inputs.subDiseaseInput;
          setFormData({
            ...formData,
            disease: '',
            subDisease: defaultFormData.subDisease,
          });
        } else {
          filteredSubDiseases = inputs.subDiseaseInput.filter(
            (subDisease) => subDisease.diseaseId === value
          );

          setDisableSubDisease(false);
        }
        setDropdowns({
          ...dropdowns,
          subDiseasesDrpDwn: filteredSubDiseases,
        });
        break;
      case "domain":
        if (value === "") {
          setDisableSubDomain(true);
          filteredSubDomains = inputs.subDomainInput;
          setFormData({
            ...formData,
            domain: defaultFormData.domain,
            subDomain: defaultFormData.subDomain,
          });
        } else {
          filteredSubDomains = inputs.subDomainInput.filter(
            (subDomain) => subDomain.domainId === value
          );
          setDisableSubDomain(false);
        }

        setDropdowns({
          ...dropdowns,
          subDomainsDrpDwn: filteredSubDomains,
        });
        break;
      default:
        break;
    }
  };

  // console.log(formData);

  const buttonSearchHandler = async (e) => {
    e.preventDefault();

    setIsSearching(true);
    setSearchResults(null);

    const searchResult = await getSearchResult("CdeCatalog", formData);
    setSearchResults(searchResult);
    setIsSearching(false);
  };

  const buttonClearHandler = (e) => {
    setFormData({ ...defaultFormData });
    setDropdowns({
      ...dropdowns,
      //subDiseasesDrpDwn: [],
      subDomainsDrpDwn: [],
    });
    //setDisableSubDisease(true);
    setDisableSubDomain(true);
    setSearchResults(null);
    setIsSearching(false);
  };

  return (
    <>
      <BodyHeader pageHeader="CDE Catalog" />
      <NotificationBar notifications={notifications} closeHandler={removeNotification} />
      <FormBuilderCart />
      <ViewHeader>
        <p><b>***Please contact the NINDS CDE Team (<a href="mailto:NINDSCDE@emmes.com">NINDSCDE@emmes.com</a>) if you encounter any search difficulties.***</b></p>
        <p>The CDE Catalog is a directory of the available NINDS CDEs. Users can search the Catalog to isolate a subset of the CDEs (e.g., all stroke-specific CDEs, etc.), and to view and download details about the CDEs.</p>
        <p>Select any filter below to search the CDE Catalog.</p>
        <div className="instructions-panel">
          <p><strong>Please read before starting your search:</strong></p>
          <p>For best results, <strong>clear form between searches</strong>. In addition, when specifying NeuroRehab or Sport-Related Concussion (SRC) as the Disease, please select a Subdisease as well.</p>
          <p>Before specifying any other search criteria, <strong>click either “Show All” or a specific disorder name</strong> under Disease Name.</p>
        </div>
        <p>NeuroRehab Comprehensive includes all NeuroRehab CDE recommendations. NeuroRehab General includes all NeuroRehab CDE recommendations that are not disease specific. All other NeuroRehab Subdiseases include recommendations specific to existing NINDS CDE project disorders.</p>
        <p>Choose your Time Frame for your SRC study (Acute (time of injury until 72 hours), Subacute (after 72 hours to 3 months), and Persistent/Chronic (3 months and greater post-concussion) or Comprehensive if your study falls across the study time frames.</p>
      </ViewHeader>
      <h4>Search Form</h4>
      <div className="cde-catalog-view">
        <div className="cde-view-filters">
          <form id="views-exposed-form-cde-catalog-page">
            <InputList
              id="disease"
              labelText="Disease Name"
              changeHandler={(e, val) => handleChange(e, "disease", val)}
              inputArr={dropdowns.diseasesDrpDwn}
              selectedValue={formData.disease}
            />
            <InputSelect
              id="subDisease"
              labelText="Subdisease Name"
              changeHandler={(e) =>
                setFormData({ ...formData, subDisease: e.target.value })
              }
              inputArr={dropdowns.subDiseasesDrpDwn}
              //disableVal={disableSubDisease}
              selectedValue={formData.subDisease}
            />
            <InputSelect
              id="domain"
              labelText="Domain Name"
              changeHandler={(e) => handleChange(e, "domain")}
              inputArr={dropdowns.domainsDrpDwn}
              selectedValue={formData.domain}
              tooltip="Filters CDEs related by category"
            />
            <InputSelect
              id="subDomain"
              labelText="Subdomain Name"
              changeHandler={(e) =>
                setFormData({ ...formData, subDomain: e.target.value })
              }
              inputArr={dropdowns.subDomainsDrpDwn}
              disableVal={disableSubDomain}
              selectedValue={formData.subDomain}
              tooltip="List only includes subdomains that have related CDEs"
            />

            <InputSelect
              id="crf"
              labelText="CRF Name"
              changeHandler={(e) =>
                setFormData({ ...formData, crf: e.target.value })
              }
              inputArr={inputs.crfInput}
              tooltip="List only includes CRFs that have related CDEs"
            />

            <InputSelect
              id="copyright"
              labelText="From ©, ® or TM"
              changeHandler={(e) =>
                setFormData({ ...formData, copyright: e.target.value })
              }
              inputArr={["True", "False"]}
              selectedValue={formData.copyright}
              tooltip="Filters only CDEs from copyrighted or trademarked CRFs or Instruments, or only public-domain CDEs"
            />

            <InputSelect
              id="classification"
              labelText="Classification"
              changeHandler={(e) =>
                setFormData({ ...formData, classification: e.target.value })
              }
              inputArr={[
                "Core",
                "Exploratory",
                "NeuroRehab Core",
                "NeuroRehab Sport Related Concussion Core",
                "NeuroRehab Supplemental-Highly Recommended",
                "Supplemental",
                "Supplemental-Highly Recommended",
              ]}
              tooltip="Level of importance for a CDE / measure as it related to usage in clinical research studies, assigned by subject matter experts"
            />

            <InputSelect
              id="population"
              labelText="Population"
              changeHandler={(e) =>
                setFormData({ ...formData, population: e.target.value })
              }
              inputArr={["Adult", "Adult;Pediatric", "Pediatric"]}
              tooltip="The target population group of the CDE / measure (Adult, Pediatric, or both)"
            />

            <InputText
              id="cdeName"
              labelText="CDE Name"
              inputValue={formData.cde}
              changeHandler={(e) =>
                setFormData({ ...formData, cde: e.target.value })
              }
            />

            <InputText
              id="keyword"
              labelText="Keyword"
              inputValue={formData.keyword}
              changeHandler={(e) =>
                setFormData({ ...formData, keyword: e.target.value })
              }
            />

            <div className="button-section">
              <button
                type="submit"
                id="submit"
                value="Search"
                onClick={(e) => buttonSearchHandler(e)}
              >
                Search
              </button>

              <button
                type="reset"
                id="clear"
                value="clear"
                onClick={(e) => buttonClearHandler(e)}
              >
                Clear
              </button>
            </div>
          </form>
        </div>
        <div className="cde-view-content">
          {isSearching && <Spinner />}
          {searchResults && (
            <ResultsTable data={searchResults} reportType="CDE" notificationsContext={restNotifications} />
          )}
        </div>
      </div>
      <ViewHeader>
        <p>The NINDS CDE Team does not post proprietary instruments/scales recommended by the CDE Working Groups on this website. This includes, but is not limited to, copyrighted or trademarked instruments/scales. Information about recommended instruments can be found in the Notice of Copyright (NOC) documents under ‘CRF Module/Guideline’ on each disorder’s data standards page. For any questions regarding these instruments/scales please contact the corresponding owner/author. The NINDS CDE Team is not responsible for the availability or content of these external sites, nor does the NINDS CDE Team endorse, warrant or guarantee the products, services or information described or offered at these other internet sites.</p>
      </ViewHeader>
    </>
  );
}
